'use client';

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ThreatAsset {
  id: string;
  caseId: string;
  asset: string;
  assetType: string;
  chain: string;
  threatType: string;
  riskLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'ACTIVE' | 'MONITORING' | 'CLOSED';
  syndicate: string | null;
  description: string | null;
  notes: string | null;
  relatedAssetsCount: number;
  dateAdded: string;
  lastUpdated: string;
}

function mapRow(row: Record<string, unknown>): ThreatAsset {
  return {
    id: row.id as string,
    caseId: row.case_id as string,
    asset: row.asset as string,
    assetType: row.asset_type as string,
    chain: row.chain as string,
    threatType: row.threat_type as string,
    riskLevel: row.risk_level as ThreatAsset['riskLevel'],
    status: row.status as ThreatAsset['status'],
    syndicate: row.syndicate as string | null,
    description: row.description as string | null,
    notes: row.notes as string | null,
    relatedAssetsCount: (row.related_assets_count as number) ?? 0,
    dateAdded: row.date_added as string,
    lastUpdated: row.last_updated as string,
  };
}

const LEVEL_STYLES: Record<string, { badge: string; dot: string }> = {
  CRITICAL: { badge: 'text-primary border-primary/40 bg-primary/10', dot: 'bg-primary' },
  HIGH: { badge: 'text-orange-400 border-orange-400/40 bg-orange-400/10', dot: 'bg-orange-400' },
  MEDIUM: { badge: 'text-yellow-400 border-yellow-400/40 bg-yellow-400/10', dot: 'bg-yellow-400' },
  LOW: { badge: 'text-green-400 border-green-400/40 bg-green-400/10', dot: 'bg-green-400' },
};

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'text-primary',
  MONITORING: 'text-yellow-400',
  CLOSED: 'text-muted-foreground',
};

type FilterLevel = 'ALL' | 'CRITICAL' | 'HIGH' | 'MEDIUM';
type SearchResult = 'matched' | 'unindexed' | null;

const PAGE_SIZE = 12;

function formatDate(iso: string): string {
  try {
    return iso.slice(0, 10);
  } catch {
    return iso;
  }
}

function ThreatRegistryInner() {
  const searchParams = useSearchParams();
  const [query, setQuery] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResult>(null);
  const [matchedThreat, setMatchedThreat] = useState<ThreatAsset | null>(null);
  const [filter, setFilter] = useState<FilterLevel>('ALL');
  const [threats, setThreats] = useState<ThreatAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Fetch threats with pagination
  const fetchThreats = useCallback(async (level: FilterLevel, page: number) => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase
        .from('threat_registry')
        .select('*', { count: 'exact' })
        .order('date_added', { ascending: false })
        .range(from, to);

      if (level !== 'ALL') {
        q = q.eq('risk_level', level);
      }

      const { data, error: err, count } = await q;

      if (err) {
        setError('Failed to load threat registry. Please try again.');
        return;
      }

      setThreats((data ?? []).map(mapRow));
      setTotalCount(count ?? 0);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  useEffect(() => {
    fetchThreats(filter, currentPage);
  }, [filter, currentPage, fetchThreats]);

  // Handle ?q= param from Verify page
  useEffect(() => {
    let q = searchParams.get('q');
    if (q) {
      // Strip protocol prefix so registry filter matches correctly
      q = q.replace(/^https?:\/\//i, '');
      setQuery(q);
      runSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (q: string) => {
    if (!q.trim()) {
      setSearchResult(null);
      setMatchedThreat(null);
      return;
    }

    try {
      const supabase = createClient();
      const { data, error: err } = await supabase
        .from('threat_registry')
        .select('*')
        .or(`asset.ilike.%${q}%,case_id.ilike.%${q}%`)
        .limit(1)
        .maybeSingle();

      if (err) {
        setSearchResult('unindexed');
        setMatchedThreat(null);
        return;
      }

      if (data) {
        setSearchResult('matched');
        setMatchedThreat(mapRow(data as Record<string, unknown>));
      } else {
        setSearchResult('unindexed');
        setMatchedThreat(null);
      }
    } catch {
      setSearchResult('unindexed');
      setMatchedThreat(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Build page number array with ellipsis
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | 'ellipsis')[] = [1];
    if (currentPage > 3) pages.push('ellipsis');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('ellipsis');
    pages.push(totalPages);
    return pages;
  };

  return (
    <section className="py-12 md:py-20 px-4" aria-label="ForensicDesk threat intelligence registry">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        <div
          className="absolute top-[5%] right-[-5%] w-72 h-72 rounded-full"
          style={{ background: 'rgba(220,38,38,0.04)', filter: 'blur(100px)' }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="bracket-tag text-primary">[THREAT REGISTRY // LIVE DATABASE]</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3">
            Intelligence<br /><span className="text-gradient-crimson">Threat Registry</span>
          </h1>
          <p className="text-muted-foreground font-light max-w-2xl">
            Cross-reference suspicious wallet addresses, domains, and transaction hashes against
            ForensicDesk&apos;s verified syndicate intelligence database. Updated continuously.
          </p>
        </div>

        {/* Search bar */}
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-3 mb-6"
          aria-label="Threat registry search"
        >
          <div className="flex-1 flex items-center gap-3 border border-border bg-input px-4 py-3 focus-within:border-primary transition-colors">
            <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter wallet address, domain, TX hash, or threat ID..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none terminal-text min-w-0"
              aria-label="Search threat registry"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); setSearchResult(null); setMatchedThreat(null); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <Icon name="XMarkIcon" size={14} />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="shine-effect flex items-center justify-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-6 py-3 bracket-tag transition-colors duration-200 min-h-[44px] whitespace-nowrap"
          >
            SCAN DATABASE
          </button>
        </form>

        {/* Search result — matched */}
        {searchResult === 'matched' && matchedThreat && (
          <div className="mb-8 border border-primary glass-card-crimson p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-primary" aria-hidden="true" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-2 h-2 bg-primary threat-pulse" />
                  <span className="bracket-tag text-primary font-black">[VERIFIED THREAT] — ASSET FOUND IN DATABASE</span>
                </div>
                <div className="text-sm font-medium text-foreground terminal-text mb-1 truncate max-w-lg">
                  {matchedThreat.asset}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="bracket-tag text-muted-foreground">[{matchedThreat.caseId}]</span>
                  {matchedThreat.syndicate && (
                    <span className="bracket-tag text-muted-foreground">[{matchedThreat.syndicate}]</span>
                  )}
                  <span className={`bracket-tag border px-2 py-0.5 ${LEVEL_STYLES[matchedThreat.riskLevel]?.badge}`}>
                    {matchedThreat.riskLevel}
                  </span>
                </div>
              </div>
              <Link
                href="/intake"
                className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-5 py-3 bracket-tag transition-colors duration-200 whitespace-nowrap shrink-0 min-h-[44px]"
              >
                SECURE TACTICAL TRIAGE REQUIRED
                <Icon name="ArrowRightIcon" size={14} />
              </Link>
            </div>
          </div>
        )}

        {/* Search result — unindexed */}
        {searchResult === 'unindexed' && (
          <div className="mb-8 border border-blue-500/40 glass-card-blue p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-blue-500/60" aria-hidden="true" />
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-2 h-2 bg-blue-400" />
                  <span className="bracket-tag text-blue-400 font-black">[UNINDEXED DEVIATION] — NOT IN CURRENT DATABASE</span>
                </div>
                <p className="text-sm text-blue-300/80 font-light">
                  This asset is not currently indexed. This does not indicate safety.
                  Submit for manual intelligence verification and deep-trace analysis.
                </p>
              </div>
              <Link
                href="/intake"
                className="flex items-center gap-2 border border-blue-500/50 hover:border-blue-400 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-5 py-3 bracket-tag transition-all duration-200 whitespace-nowrap shrink-0 min-h-[44px]"
              >
                SUBMIT FOR MANUAL VERIFICATION
                <Icon name="ArrowRightIcon" size={14} />
              </Link>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1" role="tablist" aria-label="Filter by threat level">
          {(['ALL', 'CRITICAL', 'HIGH', 'MEDIUM'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              role="tab"
              aria-selected={filter === f}
              className={`bracket-tag px-4 py-2 border transition-all duration-200 whitespace-nowrap min-h-[44px] ${
                filter === f
                  ? 'border-primary text-primary bg-primary/5' :'border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground'
              }`}
            >
              [{f}]
            </button>
          ))}
          <div className="ml-auto bracket-tag text-muted-foreground shrink-0">
            {loading ? '...' : `${totalCount} RECORDS`}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="py-20 text-center">
            <div className="inline-flex items-center gap-3 text-muted-foreground">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="bracket-tag">[QUERYING INTELLIGENCE DATABASE...]</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="py-12 text-center border border-border glass-card">
            <Icon name="ExclamationTriangleIcon" size={28} className="text-primary mx-auto mb-3" />
            <div className="bracket-tag text-primary mb-2">[DATABASE ERROR]</div>
            <p className="text-sm text-muted-foreground font-light">{error}</p>
            <button
              onClick={() => fetchThreats(filter, currentPage)}
              className="mt-4 bracket-tag text-xs text-muted-foreground hover:text-foreground border border-border px-4 py-2 transition-colors"
            >
              RETRY
            </button>
          </div>
        )}

        {/* Threat grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {threats.map((threat, i) => (
              <article
                key={threat.id}
                className="group glass-card border border-border p-5 capability-card-hover flex flex-col gap-4 animate-fade-in-up relative overflow-hidden"
                style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'forwards' }}
              >
                {/* Top scan line */}
                <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

                {/* Header row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-1">
                    <span className="bracket-tag text-muted-foreground">{threat.caseId}</span>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${LEVEL_STYLES[threat.riskLevel]?.dot} threat-pulse`} />
                      <span className={`bracket-tag border px-2 py-0.5 ${LEVEL_STYLES[threat.riskLevel]?.badge}`}>
                        {threat.riskLevel}
                      </span>
                    </div>
                  </div>
                  <span className={`bracket-tag ${STATUS_STYLES[threat.status]}`}>[{threat.status}]</span>
                </div>

                {/* Asset */}
                <div>
                  <div className="bracket-tag text-muted-foreground mb-1">[{threat.assetType} // {threat.chain}]</div>
                  <div className="text-sm font-medium text-foreground terminal-text break-all leading-tight">
                    {threat.asset}
                  </div>
                </div>

                {/* Threat type + syndicate + description */}
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {threat.syndicate && (
                      <span className="bracket-tag text-primary">[{threat.syndicate}]</span>
                    )}
                    <span className="bracket-tag text-muted-foreground text-[10px]">[{threat.threatType}]</span>
                  </div>
                  {threat.description && (
                    <p className="text-xs text-muted-foreground font-light leading-relaxed line-clamp-3">
                      {threat.description}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 pt-3 border-t border-border mt-auto">
                  <div className="flex flex-col gap-1">
                    <time dateTime={threat.lastUpdated} className="bracket-tag text-muted-foreground text-[10px]">
                      UPDATED: {formatDate(threat.lastUpdated)}
                    </time>
                    <span className="bracket-tag text-muted-foreground text-[10px]">
                      {threat.relatedAssetsCount} RELATED ASSETS
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && !error && threats.length === 0 && (
          <div className="text-center py-16 border border-border glass-card">
            <Icon name="MagnifyingGlassIcon" size={32} className="text-muted-foreground mx-auto mb-3" />
            <div className="bracket-tag text-muted-foreground mb-2">[NO RECORDS MATCH CURRENT FILTER]</div>
            <p className="text-sm text-muted-foreground font-light">Adjust filter or search parameters.</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <nav
            className="mt-8 flex items-center justify-between gap-4"
            aria-label="Threat registry pagination"
          >
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-2 bracket-tag text-xs border border-border px-4 py-2 text-muted-foreground hover:border-primary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-h-[40px]"
            >
              <Icon name="ChevronLeftIcon" size={14} />
              PREV
            </button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, idx) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="bracket-tag text-muted-foreground px-2 text-xs">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page as number)}
                    aria-current={currentPage === page ? 'page' : undefined}
                    className={`w-9 h-9 bracket-tag text-xs border transition-colors duration-200 ${
                      currentPage === page
                        ? 'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 bracket-tag text-xs border border-border px-4 py-2 text-muted-foreground hover:border-primary hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-h-[40px]"
            >
              NEXT
              <Icon name="ChevronRightIcon" size={14} />
            </button>
          </nav>
        )}

        {/* Page info */}
        {!loading && !error && totalPages > 1 && (
          <div className="mt-3 text-center bracket-tag text-muted-foreground text-[10px]">
            PAGE {currentPage} OF {totalPages} — SHOWING {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} OF {totalCount} RECORDS
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-12 border border-border glass-card p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="bracket-tag text-muted-foreground mb-1">[SUBMIT FOR ANALYSIS]</div>
            <p className="text-sm font-medium text-foreground">
              Asset not in registry? Submit for manual deep-trace analysis.
            </p>
          </div>
          <Link
            href="/intake"
            className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-6 py-3 bracket-tag transition-colors duration-200 whitespace-nowrap min-h-[44px]"
          >
            SUBMIT ASSET FOR ANALYSIS
            <Icon name="ArrowRightIcon" size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function ThreatRegistryClient() {
  return (
    <Suspense fallback={<div className="py-20 text-center text-zinc-400 terminal-text">[LOADING REGISTRY...]</div>}>
      <ThreatRegistryInner />
    </Suspense>
  );
}